import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import * as CompanyEmailValidator from "company-email-validator";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import EmailAddress, { InvalidEmailAddressError } from "../email-address";

const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
    width: "100%",
  },
  marginBottom: theme.spacing(1.5),
  ":first-of-type": {
    marginRight: theme.spacing(1.5),
  },
}));

interface EmailInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  initialValue?: EmailAddress;
  allowNonCompanyDomains?: boolean;
  /**
   * This method is called when the name instance is modified. This callback is only
   * registered initially, to reregister the handler, change the key
   * @returns ({@link EmailAddress}, undefined) when successful
   * @returns (undefined, errorMessage: string) when unsuccessful
   */
  onChange?: (emailAddress?: EmailAddress, errorMessage?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export default function EmailAddressInput(props: Readonly<EmailInputProps>) {
  const {
    idPrefix,
    className,
    variant,
    disabled,
    initialValue,
    allowNonCompanyDomains,
    onChange,
    onKeyDown,
    ...otherProps
  } = props;

  const [emailText, setEmailText] = useState<string>(initialValue?.value ?? "");
  const [emailError, setEmailError] = useState<string>("");

  const emailIsDirty = useRef<boolean>(false);

  const debouncedValidate = useMemo(() => 
    debounce((emailValue?: string, isDirty?: boolean) => {
    let email: EmailAddress | undefined;
    let newErrors: string = "";

    if(!isDirty) return;
    try {
      email = buildEmailAddress(emailValue, allowNonCompanyDomains);
      setEmailError("");
    } catch (error) {
      if (error instanceof InvalidEmailAddressError) {
        newErrors = error.userFriendlyMessage;
        setEmailError(newErrors);
      } else {
        throw error;
      }
    } finally {
      onChange?.(email, newErrors);
    }
    }),
  // We cannot guarantee onChange being stable, if it changes, change the key to recreate the component
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [allowNonCompanyDomains]);

  useEffect(() => {
    debouncedValidate(emailText, emailIsDirty.current);
    return () => debouncedValidate.cancel();
  }, [emailText, debouncedValidate]);

  return (
    <InputField
      id={idPrefix ? idPrefix + "-email-address-input" : "email-address-input"}
      className={className}
      label="Email Address"
      onChange={(event) => {
        setEmailText(prev => {
          if(emailIsDirty.current) return event.target.value;
          if(prev !== event.target.value)
          {
            emailIsDirty.current = true;
            return event.target.value;
          }
          return prev
        });
      }}
      required
      disabled={disabled}
      value={emailText}
      error={!!emailError}
      helperText={emailError}
      variant={variant}
      autoComplete="account-email"
      type="email"
      onKeyDown={onKeyDown}
      {...otherProps}
    />
  );
}

function buildEmailAddress(inputText?: string, allowNonCompanyDomains?: boolean): EmailAddress | undefined {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!inputText) {
    throw new InvalidEmailAddressError("Email address is required", inputText);
  }

  if (!emailRegex.test(inputText)) {
    throw new InvalidEmailAddressError(EmailAddress.VALID_EMAIL_FORMAT_MESSAGE, inputText);
  }

  if (
    !allowNonCompanyDomains &&
    !inputText.includes("@example.com") &&
    !CompanyEmailValidator.isCompanyEmail(inputText)
  ) {
    throw new InvalidEmailAddressError("Email address must be a company email domain", inputText);
  }
  return new EmailAddress(inputText);
}
